import { MobileDialog, ModalContent, PrimaryButton, SecondaryButton, StickyModalActions } from '@/styles/blocks';
import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Grid, TextField } from '@material-ui/core';
import { Body1, Header1, Header2, ItemCheckbox } from '@/components/OrderDetailsPage/Crossdocking/blocks';
import { colors } from '@/styles';
import { OnwardCheckbox } from '@/components/ShipmentForm/blocks';
import FileUpload from '../FileUpload';
import uploadPhotoAsync from '@/utilities/uploadPhotoAsync';
import { captureException } from '@sentry/react';
import Signature from '../Signature';
import { buildTrackingEvents } from '@/utilities/trackingEvents';

const FILE_TYPES = {
    'application/pdf': 'pdf',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
};

const ChecklistModal = ({
    open,
    title,
    itemsHelperText,
    itemEvent,
    // Set only where a return item's meaning inverts — see Crossdocking/itemEvents.js.
    returnItemEvent,
    timestamps = [],
    showItems,
    order,
    items = [],
    documents = [],
    signatures = [],
    callbacks,
}) => {
    const [override, setSelected] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [uploadedSignatures, setUploadedSignatures] = useState({});
    const [loading, setLoading] = useState(false);
    const [timestampInputs, setTimestampInputs] = useState({});

    const toLocalInputValue = (dateOrIso) => {
        if (!dateOrIso) return '';
        const d = typeof dateOrIso === 'string' ? new Date(dateOrIso) : dateOrIso;
        if (Number.isNaN(d.getTime())) return '';
        const tzOffsetMs = d.getTimezoneOffset() * 60 * 1000;
        // datetime-local expects local time without timezone
        return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16);
    };

    useEffect(() => {
        if (!open) return;
        // initialize required timestamps to "now" unless already set
        setTimestampInputs((prev) => {
            const NOW = new Date();
            const next = { ...prev };
            for (const ts of timestamps || []) {
                if (!next[ts.field]) {
                    next[ts.field] = toLocalInputValue(order?.[ts.field] || NOW);
                }
            }
            return next;
        });
    }, [open, timestamps, order]);

    // Intangible items are informational only (no physical footprint), so they are never checked
    // off: they are excluded from the list and from item tracking events.
    const tangibleItems = useMemo(() => items.filter((item) => !item.intangible), [items]);

    const selected = useMemo(() => {
        return {
            ...Object.fromEntries(tangibleItems.map((item) => [item.item_id, false])),
            ...override,
        };
    }, [override, tangibleItems]);

    const hasItemsSection = useMemo(() => {
        return (showItems ?? !!itemEvent) && tangibleItems.length > 0;
    }, [showItems, itemEvent, tangibleItems]);

    const hasTimesSection = useMemo(() => {
        return (timestamps || []).length > 0;
    }, [timestamps]);

    const hasDocumentsSection = useMemo(() => {
        return (documents || []).length > 0;
    }, [documents]);

    const hasSignaturesSection = useMemo(() => {
        return (signatures || []).length > 0;
    }, [signatures]);

    // Item checkboxes are a record of what physically moved, not a completion gate: a step may close
    // with some — or none — of its items checked, and only the checked ones get tracking events.
    // Documents, signatures and timestamps still gate, since those are the step's own paperwork.
    const isValid = useMemo(() => {
        return (
            (!hasDocumentsSection ||
                documents.every((doc) => !doc.required || uploadedFiles?.[doc.field]?.length > 0)) &&
            (!hasSignaturesSection || signatures.every((sig) => !sig.required || !!uploadedSignatures?.[sig.field])) &&
            (!hasTimesSection || (timestamps || []).every((ts) => !ts.required || !!timestampInputs?.[ts.field]))
        );
    }, [
        uploadedFiles,
        hasDocumentsSection,
        documents,
        uploadedSignatures,
        hasSignaturesSection,
        signatures,
        hasTimesSection,
        timestamps,
        timestampInputs,
    ]);

    const onConfirm = async () => {
        setLoading(true);
        try {
            let orderUpdates = {};

            for (const [field, files] of Object.entries(uploadedFiles)) {
                const databaseFiles = [];
                for (const file of files) {
                    const firebaseFile = await uploadPhotoAsync(file.src, field, FILE_TYPES[file.type]);
                    databaseFiles.push({ url: firebaseFile, environment: 'web' });
                }
                orderUpdates[field] = [...(order[field] || []), ...databaseFiles];
            }

            for (const [field, file] of Object.entries(uploadedSignatures)) {
                const databaseFile = await uploadPhotoAsync(file.src, field, FILE_TYPES[file.type]);
                orderUpdates[field] = databaseFile;
            }

            for (const ts of timestamps || []) {
                const value = timestampInputs?.[ts.field];
                if (ts.required && !value) {
                    throw new Error(`Missing required timestamp: ${ts.name || ts.field}`);
                }
                if (value) {
                    orderUpdates[ts.field] = new Date(value).toISOString();
                }
            }

            const trackingEvents = buildTrackingEvents({
                items: tangibleItems.filter((item) => !!selected[item.item_id]),
                eventType: itemEvent,
                returnEventType: returnItemEvent,
                skipExisting: true,
            });

            await callbacks.onConfirm(orderUpdates, trackingEvents);

            setSelected({});
            setUploadedFiles({});
            setUploadedSignatures({});
            setTimestampInputs({});
            setLoading(false);
        } catch (e) {
            console.error(e);
            captureException(e);
            callbacks?.onError?.(e);
            setLoading(false);
        }
    };

    return (
        <MobileDialog
            open={open}
            onClose={() => {
                setSelected({});
                callbacks.onClose();
            }}
        >
            <ModalContent
                css={css`
                    width: auto;
                `}
            >
                <Grid
                    container
                    css={css`
                        margin-bottom: 12px;
                    `}
                >
                    <Header2>{title}</Header2>
                </Grid>
                {hasItemsSection ? (
                    <>
                        <Grid
                            container
                            css={css`
                                margin-top: 30px;
                                margin-bottom: 4px;
                            `}
                        >
                            <Header1>Items</Header1>
                        </Grid>
                        <Grid
                            container
                            css={css`
                                margin-bottom: 8px;
                                color: ${colors.greys[0]};
                            `}
                        >
                            <Body1>{itemsHelperText || ''}</Body1>
                        </Grid>
                        <Grid
                            direction="row"
                            container
                            css={css`
                                margin-bottom: 30px;
                            `}
                        >
                            <Grid direction="column" container>
                                <Grid
                                    direction="row"
                                    container
                                    item
                                    css={css`
                                        margin-bottom: 8px;
                                    `}
                                >
                                    <Grid
                                        direction="column"
                                        container
                                        css={css`
                                            flex: 0;
                                            flex-basis: 0;
                                            justify-content: center;
                                        `}
                                    >
                                        <OnwardCheckbox
                                            checked={tangibleItems
                                                .map((item) => item.item_id)
                                                .every((id) => selected[id])}
                                            onChange={() => {
                                                setSelected((prev) => {
                                                    const ids = tangibleItems.map((item) => item.item_id);
                                                    const checked = !ids.every((id) => selected[id]);

                                                    return {
                                                        ...prev,
                                                        ...Object.fromEntries(
                                                            ids.map((id) => [id, checked ? true : false])
                                                        ),
                                                    };
                                                });
                                            }}
                                        />
                                    </Grid>
                                    <Grid
                                        direction="column"
                                        container
                                        css={css`
                                            flex: 1;
                                            flex-basis: 0;
                                            justify-content: center;
                                        `}
                                    >
                                        <Header1>Select All</Header1>
                                    </Grid>
                                </Grid>

                                {tangibleItems.map((item) => {
                                    return (
                                        <ItemCheckbox
                                            key={item.item_id}
                                            item={item}
                                            checked={selected[item.item_id]}
                                            callbacks={{
                                                onCheck: (id) =>
                                                    setSelected((prev) => {
                                                        return {
                                                            ...prev,
                                                            [id]: !prev[id],
                                                        };
                                                    }),
                                            }}
                                        />
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </>
                ) : null}
                {hasTimesSection && (
                    <>
                        <Grid
                            container
                            css={css`
                                margin-top: 30px;
                                margin-bottom: 4px;
                            `}
                        >
                            <Header1>Times</Header1>
                        </Grid>
                        <Grid container direction="column">
                            {(timestamps || []).map((ts) => (
                                <Grid
                                    key={ts.field}
                                    container
                                    direction="column"
                                    css={css`
                                        margin-bottom: 12px;
                                    `}
                                >
                                    <Body1
                                        css={css`
                                            margin-bottom: 6px;
                                            color: ${colors.greys[0]};
                                        `}
                                    >
                                        {ts.name || ts.field}
                                        {ts.required ? ' *' : ''}
                                    </Body1>
                                    <TextField
                                        type="datetime-local"
                                        value={timestampInputs?.[ts.field] || ''}
                                        onChange={(e) =>
                                            setTimestampInputs((prev) => ({
                                                ...prev,
                                                [ts.field]: e.target.value,
                                            }))
                                        }
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}
                {hasDocumentsSection && (
                    <>
                        <Grid
                            container
                            css={css`
                                margin-top: 30px;
                                margin-bottom: 4px;
                            `}
                        >
                            <Header1>Documents</Header1>
                        </Grid>
                        <Grid
                            container
                            css={css`
                                margin-bottom: 8px;
                                color: ${colors.greys[0]};
                            `}
                        >
                            <Body1>Upload all required documents.</Body1>
                        </Grid>
                        {documents.map((doc) => (
                            <FileUpload
                                key={`file-upload-${doc.field}`}
                                name={doc.name}
                                required={doc.required}
                                files={uploadedFiles[doc.field] || []}
                                callbacks={{
                                    setFiles: (newFiles) =>
                                        setUploadedFiles((prev) => ({ ...prev, [doc.field]: newFiles })),
                                }}
                            />
                        ))}
                    </>
                )}

                {hasSignaturesSection && (
                    <>
                        <Grid
                            container
                            css={css`
                                margin-top: 30px;
                                margin-bottom: 4px;
                            `}
                        >
                            <Header1>Signatures</Header1>
                        </Grid>
                        {signatures.map((sig) => (
                            <Signature
                                key={`signature-${sig.field}`}
                                name={sig.name}
                                required={sig.required}
                                file={uploadedFiles[sig.field] || {}}
                                callbacks={{
                                    setFile: (newFile) =>
                                        setUploadedSignatures((prev) => ({ ...prev, [sig.field]: newFile })),
                                }}
                            />
                        ))}
                    </>
                )}
            </ModalContent>
            <StickyModalActions>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <SecondaryButton
                        onClick={() => {
                            setSelected({});
                            callbacks.onClose();
                        }}
                    >
                        Cancel
                    </SecondaryButton>
                </Grid>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <PrimaryButton disabled={!isValid || loading} onClick={onConfirm}>
                        Complete
                    </PrimaryButton>
                </Grid>
            </StickyModalActions>
        </MobileDialog>
    );
};

export default ChecklistModal;
